// Users Manager Component (Admin management)
import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Loader2, Users, UserCheck } from 'lucide-react';
import { Button } from '../ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const UsersManager = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newAdminEmail, setNewAdminEmail] = useState('');
  const [adding, setAdding] = useState(false);

  useEffect(() => { fetchUsers(); }, []);

  const fetchUsers = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/users`, { credentials: 'include' });
      if (response.ok) { const data = await response.json(); setUsers(data || []); }
    } catch (err) { console.error('Error fetching users:', err); }
    finally { setLoading(false); }
  };

  const handleAddAdmin = async () => {
    if (!newAdminEmail.trim()) return;
    setAdding(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/users`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify({ email: newAdminEmail.trim() })
      });
      if (response.ok) { setNewAdminEmail(''); fetchUsers(); }
      else { const err = await response.json(); alert(err.detail || 'Erreur'); }
    } catch (err) { console.error('Error adding admin:', err); }
    finally { setAdding(false); }
  };

  const handleRemoveAdmin = async (userId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir retirer les droits admin à cet utilisateur ?')) return;
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/users/${userId}`, { method: 'DELETE', credentials: 'include' });
      if (response.ok) { fetchUsers(); }
      else { const err = await response.json(); alert(err.detail || 'Erreur'); }
    } catch (err) { console.error('Error removing admin:', err); }
  };

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>;
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-light text-slate-800">Administrateurs</h2>
          <p className="text-sm text-slate-500 mt-1">Gérez les utilisateurs ayant accès au panneau d'administration</p>
        </div>
      </div>

      {/* Add new admin form */}
      <div className="border border-slate-200 rounded-lg p-4 mb-6">
        <h3 className="font-medium text-slate-800 mb-3">Ajouter un administrateur</h3>
        <p className="text-sm text-slate-500 mb-4">L'utilisateur doit d'abord se connecter avec son compte Google pour être ajouté comme admin.</p>
        <div className="flex gap-2">
          <input type="email" value={newAdminEmail} onChange={(e) => setNewAdminEmail(e.target.value)} placeholder="email@exemple.com" className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm" onKeyPress={(e) => e.key === 'Enter' && handleAddAdmin()} />
          <Button onClick={handleAddAdmin} disabled={adding || !newAdminEmail.trim()}>
            {adding ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Ajout...</> : <><Plus className="w-4 h-4 mr-2" /> Ajouter</>}
          </Button>
        </div>
      </div>

      {/* Users list */}
      <div className="space-y-4">
        {users.map((user) => (
          <div key={user.user_id} className="border border-slate-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                {user.picture ? (
                  <img src={user.picture} alt="" className="w-10 h-10 rounded-full" />
                ) : (
                  <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center">
                    <Users className="w-5 h-5 text-slate-400" />
                  </div>
                )}
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-medium text-slate-800">{user.name || 'Utilisateur'}</h3>
                    {user.is_admin && <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs flex items-center gap-1"><UserCheck className="w-3 h-3" /> Admin</span>}
                  </div>
                  <p className="text-sm text-slate-500">{user.email}</p>
                </div>
              </div>
              <Button variant="ghost" size="sm" onClick={() => handleRemoveAdmin(user.user_id)}>
                <Trash2 className="w-4 h-4 text-red-500" />
              </Button>
            </div>
          </div>
        ))}
      </div>

      {users.length === 0 && <div className="text-center py-12 text-slate-500">Aucun administrateur configuré.</div>}

      {/* Info box */}
      <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <h3 className="font-medium text-blue-900 mb-2">ℹ️ Gestion des administrateurs</h3>
        <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
          <li>L'utilisateur doit d'abord se connecter avec Google pour apparaître dans le système</li>
          <li>Ensuite, entrez son email ci-dessus pour lui donner les droits admin</li>
          <li>Seul un admin peut ajouter d'autres admins</li>
        </ul>
      </div>
    </div>
  );
};

export default UsersManager;
